import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BadgeDollarSign, MapPin, Medal, ArrowRight, CalendarDays, Phone } from "lucide-react";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/Marquee";
import { useServices } from "@/lib/useServices";

const EASE = [0.22, 1, 0.36, 1];

const POINTS = [
  { icon: BadgeDollarSign, title: "Affordable", text: "Fair starting prices." },
  { icon: MapPin, title: "Local", text: "Located in Oak Grove & Grain Valley." },
  { icon: Medal, title: "Hard-working", text: "We take pride in getting the job done." },
];

export default function Home() {
  const services = useServices();

  return (
    <>
      <Hero />
      <EditorialMarquee />

      <section className="py-20 bg-bone">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex flex-wrap items-end justify-between gap-6 mb-10"
          >
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-limed mb-4">
                Why All-Season Crew?
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-emerald-950 leading-[1.08]">
                A team of teens building something real.
              </h2>
            </div>
            <Link
              data-testid="home-about-link"
              to="/about"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-pine hover:text-limed transition-colors duration-200"
            >
              Our story
              <ArrowRight size={15} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-5">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                className="bg-white rounded-3xl border border-emerald-950/10 shadow-xl shadow-emerald-950/5 p-6 hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-limed-light flex items-center justify-center text-pine mb-4">
                  <p.icon size={20} strokeWidth={2.2} />
                </div>
                <p className="font-display font-bold text-emerald-950">{p.title}</p>
                <p className="text-sm text-emerald-900/60 mt-1">{p.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="service-area-section"
            className="mt-10 bg-sage/50 border border-emerald-950/10 rounded-3xl p-7 sm:p-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-limed mb-3">
              Service Area
            </p>
            <h3 className="font-display font-bold text-emerald-950 text-2xl sm:text-3xl tracking-tight">
              Proudly serving our own neighborhoods.
            </h3>
            <p className="mt-3 text-base text-emerald-900/70 leading-relaxed max-w-2xl">
              We're based right here, so we can usually get to you fast — often same week. If you're
              just outside these towns, reach out and we'll see what we can do.
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4 max-w-2xl">
              <div
                data-testid="service-area-oak-grove"
                className="bg-white rounded-2xl border border-emerald-950/10 p-5 flex items-center gap-4 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-950/10 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-pine flex items-center justify-center text-limed-bright shrink-0">
                  <MapPin size={22} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-display font-bold text-emerald-950 text-lg leading-tight">Oak Grove, MO</p>
                  <p className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mt-1">Home base</p>
                </div>
              </div>
              <div
                data-testid="service-area-grain-valley"
                className="bg-white rounded-2xl border border-emerald-950/10 p-5 flex items-center gap-4 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-950/10 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-limed flex items-center justify-center text-pine-dark shrink-0">
                  <MapPin size={22} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-display font-bold text-emerald-950 text-lg leading-tight">Grain Valley, MO</p>
                  <p className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mt-1">Just down the road</p>
                </div>
              </div>
            </div>

            <a
              data-testid="service-area-phone-link"
              href="tel:8164009576"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-pine hover:text-limed transition-colors duration-200"
            >
              <Phone size={15} strokeWidth={2.5} />
              Not sure if you're in range? Call or text 816-400-9576
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-10 grid md:grid-cols-2 gap-5"
          >
            <Link
              data-testid="home-services-link"
              to="/services"
              className="group bg-pine rounded-3xl p-7 flex items-center justify-between gap-4 hover:bg-pine-dark transition-colors duration-300"
            >
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-limed-bright mb-2">
                  {services.length} services
                </p>
                <p className="font-display font-bold text-bone text-2xl">Browse everything we do</p>
                <p className="text-sm text-bone/60 mt-1.5">
                  From $15 bin cleans to full yard cleanups.
                </p>
              </div>
              <ArrowRight size={26} className="text-limed-bright shrink-0 group-hover:translate-x-1.5 transition-transform duration-300" />
            </Link>
            <Link
              data-testid="home-book-link"
              to="/schedule"
              className="group bg-limed rounded-3xl p-7 flex items-center justify-between gap-4 hover:bg-limed-bright transition-colors duration-300"
            >
              <div>
                <CalendarDays size={24} className="text-pine-dark mb-3" />
                <p className="font-display font-bold text-pine-dark text-2xl leading-tight">Book a slot</p>
                <p className="text-sm text-pine-dark/60 mt-1.5">
                  Live weekly calendar — pick your day and time.
                </p>
              </div>
              <ArrowRight size={26} className="text-pine-dark shrink-0 group-hover:translate-x-1.5 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
